import { useCallback, useState } from 'react';
import moment from 'moment';
import { Button, Text } from 'rizzui';
import { toast } from 'sonner';
import FileUpload from '../../../components/FileUpload/FileUpload';
import ModalForm from '../../../components/forms/ModalForm';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useFilters from '../../../hooks/useFilters';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { PlatformAdvanceService } from '../../../services/platform-advance/platformAdvanceService';

const columnOrder = [
    'id',
    'companyId',
    'companyName',
    'importBatch',
    'sourceFileName',
    'rowIndex',
    'studentName',
    'trainingActionCode',
    'groupCode',
    'contractCode',
    'facilitatorName',
    'incidenceName',
    'rawData',
    'createdAt',
];

const PlatformAdvancesPage = () => {
    const service = new PlatformAdvanceService();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const [files, setFiles] = useState<File[]>([]);
    const [isImporting, setIsImporting] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [selectedRawData, setSelectedRawData] = useState<Record<string, any> | null>(null);
    const canImport = userCan('import_platform_advances', 'platform_advances');
    const canExport = userCan('export_platform_advances', 'platform_advances');
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters({}, [{ field: 'createdAt', order: 'desc' }], 1, 25);

    const [response, loading, error, refetch] = useFetch(useCallback(async () => {
        const apiResponse = await service.listPlatformAdvances(filters);

        return apiResponse.getResponseData();
    }, [filters]));

    const rows = response?.data ?? [];
    const lastPage = response?.lastPage ?? 1;

    const orderedData = () => rows.map((row: any) => ({
        id: row.id,
        companyId: row.company?.id || 'N/A',
        companyName: row.company?.name || 'N/A',
        importBatch: row.importBatch || 'N/A',
        sourceFileName: row.sourceFileName || 'Sin fichero',
        rowIndex: row.rowIndex ?? 'N/A',
        studentName: row.studentName || 'N/A',
        trainingActionCode: row.trainingActionCode || 'N/A',
        groupCode: row.groupCode || 'N/A',
        contractCode: row.contractCode || 'N/A',
        facilitatorName: row.facilitatorName || 'N/A',
        incidenceName: row.incidenceName || 'N/A',
        rawData: row.rawData || null,
        createdAt: row.createdAt || null,
    }));

    const handleImport = async () => {
        const file = files[0];
        if (!file) {
            toast.error('Debes seleccionar un Excel.');
            return;
        }

        setIsImporting(true);

        try {
            const apiResponse = await (await service.importPlatformAdvances(file)).getResponseData();
            if (!apiResponse?.success) {
                handleErrors(apiResponse);
                return;
            }

            toast.success(`Importación completada: ${apiResponse.data?.rowsImported ?? 0} filas`);
            setFiles([]);
            setIsImportModalOpen(false);
            refetch();
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsImporting(false);
        }
    };

    const handleExport = async () => {
        try {
            const apiResponse = await (await service.listPlatformAdvances({
                page: 1,
                limit: 5000,
                filter_filters: {
                    search_text: filters.filter_filters?.search_text || undefined,
                },
                filter_order: [{ field: 'createdAt', order: 'DESC' }],
            })).getResponseData();

            if (!apiResponse?.success) {
                handleErrors(apiResponse);
                return;
            }

            const exportRows = apiResponse?.data?.data ?? [];
            if (exportRows.length === 0) {
                toast.error('No hay datos para exportar.');
                return;
            }

            const headers = [
                'id',
                'companyId',
                'companyName',
                'importBatch',
                'sourceFileName',
                'rowIndex',
                'studentName',
                'trainingActionCode',
                'groupCode',
                'contractCode',
                'facilitatorName',
                'incidenceName',
                'rawData',
                'createdAt',
            ];
            const csv = [
                headers.join(';'),
                ...exportRows.map((row: any) => {
                    const exportRow = {
                        id: row.id ?? '',
                        companyId: row.company?.id ?? '',
                        companyName: row.company?.name ?? '',
                        importBatch: row.importBatch ?? '',
                        sourceFileName: row.sourceFileName ?? '',
                        rowIndex: row.rowIndex ?? '',
                        studentName: row.studentName ?? '',
                        trainingActionCode: row.trainingActionCode ?? '',
                        groupCode: row.groupCode ?? '',
                        contractCode: row.contractCode ?? '',
                        facilitatorName: row.facilitatorName ?? '',
                        incidenceName: row.incidenceName ?? '',
                        rawData: row.rawData ? JSON.stringify(row.rawData) : '',
                        createdAt: row.createdAt?.date ?? row.createdAt ?? '',
                    };

                    return headers.map((header) => `"${String((exportRow as any)[header] ?? '').replace(/"/g, '""')}"`).join(';');
                }),
            ].join('\n');

            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'avances-plataforma.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            handleErrors(error);
        }
    };

    if (loading && !response) {
        return <Loader height="50vh" />;
    }

    if (!loading && error) {
        return (
            <Page container="fluid">
                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    No se ha podido cargar el listado de avances de plataforma.
                </div>
            </Page>
        );
    }

    return (
        <Page container="fluid">
            <CustomTable
                id="platform-advances-table"
                columnOrder={columnOrder}
                data={orderedData()}
                isLoading={loading}
                columnsNotShown={[
                    'companyId',
                    'importBatch',
                    'sourceFileName',
                    'rowIndex',
                    'trainingActionCode',
                    'contractCode',
                    'facilitatorName',
                    'incidenceName'
                ]}
                overrideColumns={[
                    {
                        key: 'id',
                        columnNameKey: 'ID',
                        render: (row: any) => row.row.original.id,
                    },
                    {
                        key: 'companyId',
                        columnNameKey: 'ID empresa',
                        render: (row: any) => row.row.original.companyId,
                    },
                    {
                        key: 'companyName',
                        columnNameKey: 'Empresa',
                        render: (row: any) => row.row.original.companyName,
                    },
                    {
                        key: 'importBatch',
                        columnNameKey: 'Lote importación',
                        render: (row: any) => row.row.original.importBatch,
                    },
                    {
                        key: 'sourceFileName',
                        columnNameKey: 'Fichero origen',
                        render: (row: any) => row.row.original.sourceFileName,
                    },
                    {
                        key: 'rowIndex',
                        columnNameKey: 'Fila',
                        render: (row: any) => row.row.original.rowIndex,
                    },
                    {
                        key: 'studentName',
                        columnNameKey: 'Alumno',
                        render: (row: any) => <Text className="font-bold text-primary">{row.row.original.studentName}</Text>,
                    },
                    {
                        key: 'trainingActionCode',
                        columnNameKey: 'Acción formativa',
                        render: (row: any) => row.row.original.trainingActionCode,
                    },
                    {
                        key: 'groupCode',
                        columnNameKey: 'Grupo',
                        render: (row: any) => row.row.original.groupCode,
                    },
                    {
                        key: 'contractCode',
                        columnNameKey: 'Contrato',
                        render: (row: any) => row.row.original.contractCode,
                    },
                    {
                        key: 'facilitatorName',
                        columnNameKey: 'Dinamizador',
                        render: (row: any) => row.row.original.facilitatorName,
                    },
                    {
                        key: 'incidenceName',
                        columnNameKey: 'Incidencia',
                        render: (row: any) => row.row.original.incidenceName,
                    },
                    {
                        key: 'rawData',
                        columnNameKey: 'Datos brutos',
                        render: (row: any) => (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setSelectedRawData(row.row.original.rawData || {})}
                                disabled={!row.row.original.rawData}
                            >
                                Ver
                            </Button>
                        ),
                    },
                    {
                        key: 'createdAt',
                        columnNameKey: 'Fecha creación',
                        render: (row: any) => row.row.original.createdAt?.date ? moment(row.row.original.createdAt.date).format('DD/MM/YYYY HH:mm') : 'N/A',
                    },
                ]}
                filters={filters}
                updateFilters={updateFilters}
                updateFilterOrder={updateFilterOrder}
                defaultOrder={filters.filter_order || undefined}
                paginationData={{
                    pageSize: filters.limit,
                    currentPage: filters.page,
                    pageCount: lastPage,
                    totalCount: response?.totalRegisters || rows.length,
                    handlePagination: updatePage,
                    handlePerPage: updatePageSize,
                }}
                filter={false}
            >
                <div className="flex items-center gap-2">
                    {canImport && (
                        <Button
                            onClick={() => {
                                setFiles([]);
                                setIsImportModalOpen(true);
                            }}
                        >
                            Importar
                        </Button>
                    )}
                    {canExport && (
                        <Button variant="outline" onClick={handleExport}>
                            Exportar
                        </Button>
                    )}
                    <Button variant="outline" onClick={() => refetch()}>
                        Actualizar
                    </Button>
                </div>
            </CustomTable>

            <ModalForm
                isOpen={isImportModalOpen}
                onClose={() => {
                    if (isImporting) return;
                    setIsImportModalOpen(false);
                    setFiles([]);
                }}
                title="Importar avances"
                size="lg"
            >
                <div className="space-y-4">
                    <Text className="text-sm text-gray-500">
                        Sube el fichero de seguimiento para registrar el último volcado de avances de plataforma.
                    </Text>
                    <FileUpload
                        files={files}
                        onFilesChange={(nextFiles) => setFiles(nextFiles.slice(0, 1))}
                        maxFiles={1}
                        accept={{
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                            'application/vnd.ms-excel': ['.xls'],
                        }}
                        title="Archivo Excel"
                        dropText="Arrastra el Excel de seguimiento o haz clic para seleccionarlo"
                        helpText="Formato requerido: seguimiento_alumnos_YYYYMMDD_HHMMSS.xlsx"
                    />
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setIsImportModalOpen(false);
                                setFiles([]);
                            }}
                            disabled={isImporting}
                        >
                            Cancelar
                        </Button>
                        <Button onClick={handleImport} disabled={files.length === 0 || isImporting}>
                            {isImporting ? 'Importando...' : 'Importar volcado'}
                        </Button>
                    </div>
                </div>
            </ModalForm>

            <ModalForm
                isOpen={!!selectedRawData}
                onClose={() => setSelectedRawData(null)}
                title="Datos brutos"
                size="xl"
            >
                <div className="grid gap-4 md:grid-cols-2">
                    {selectedRawData && Object.keys(selectedRawData).length > 0 ? Object.entries(selectedRawData).map(([key, value]) => (
                        <div key={key} className="rounded-md border border-gray-200 bg-gray-50 p-3">
                            <Text className="mb-1 text-xs font-semibold uppercase text-gray-500">{key}</Text>
                            <Text className="break-words text-sm text-gray-900">
                                {value === null || value === undefined || value === '' ? 'N/A' : typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </Text>
                        </div>
                    )) : (
                        <Text className="text-sm text-gray-500">Sin datos brutos disponibles.</Text>
                    )}
                </div>
            </ModalForm>
        </Page>
    );
};

export default PlatformAdvancesPage;
